import {
  Box,
  Button,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';

interface ContentRowProps {
  title: string;
  value: string;
  isLoading?: boolean;
}

type Props = {
  title: string;
  description?: string;
  button?: {
    onClick: () => void;
    text: string;
    tooltip?: string;
    disabled?: boolean;
  };
} & (
  | {
      children: React.ReactNode;
    }
  | {
      rows: Omit<ContentRowProps, 'isLoading'>[];
      isLoading?: boolean;
    }
);

function ContentRow({ title, value, isLoading }: ContentRowProps) {
  return (
    <Box
      paddingY={2.5}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      borderBottom="1px solid #e0e0e0"
      gap={1}
      sx={{
        overflowX: 'auto',
      }}>
      <Typography whiteSpace="nowrap">{title}</Typography>
      {!isLoading && <Typography whiteSpace="nowrap">{value}</Typography>}
      {isLoading && (
        <Skeleton variant="text" width={100} height={20} animation="wave" />
      )}
    </Box>
  );
}

function EditableInfoSection({
  title,
  description,
  button,
  ...content
}: Props) {
  const theme = useTheme();
  return (
    <Box className="input-group">
      <Stack spacing={0.75} width="100%">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%">
          <Typography variant="h2">{title}</Typography>
          {!!button && (
            <Tooltip
              enterTouchDelay={0}
              title={button.tooltip || undefined}
              arrow
              placement="top">
              <span>
                <Button
                  variant="link"
                  color="inherit"
                  disabled={button.disabled}
                  sx={{
                    ':disabled': {
                      pointerEvents: 'initial',
                    },
                  }}
                  onClick={button.onClick}>
                  {button.text}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
        {description && (
          <Typography variant="body1" color={theme.palette.darkGrey.main}>
            {description}
          </Typography>
        )}
      </Stack>

      <Box width="100%">
        {'children' in content
          ? content.children
          : content.rows.map(row => (
              <ContentRow
                key={row.title}
                {...row}
                isLoading={content.isLoading}
              />
            ))}
      </Box>
    </Box>
  );
}

export default EditableInfoSection;
